import { useRef, useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardFooter, CardTitle } from "@/components/ui/card";
import { SCHEMA_NAMES, type Goal } from "instantdb/types";
import { cn } from "@/lib/utils";
import { ResourceContextMenu } from "@/components/dropdowns/ResourceContextMenu";
import { Button } from "@/components/ui/button";
import { Calendar, Clock, Plus } from "lucide-react";
import { ResourceDropdown } from "@/components/dropdowns/ResourceDropdown";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { FloatingTooltip } from "@/components/ui/floating-tooltip";
import { useGoals } from "@/hooks/goals/useGoals";
import { DEFAULT_SYMBOL } from "@/config/constants";
import { useNavigate } from "@tanstack/react-router";
import { format } from "date-fns";
import { useTranslation } from "@/hooks/useTranslation";

interface GoalCardProps {
  goal: Goal;
  onHome?: boolean;
  isDragging?: boolean;
  isOverlay?: boolean;
  disableClickThrough?: boolean;
  isLibrary?: boolean;
  isCopied?: boolean;
}

export function GoalCard({
  goal,
  onHome,
  isDragging,
  isOverlay,
  disableClickThrough,
  isLibrary = false,
  isCopied: _isCopied = false,
}: GoalCardProps) {
  const { handleCopyGoal } = useGoals();
  const [height, setHeight] = useState<number | null>(null);
  const cardRef = useRef<HTMLDivElement | null>(null);
  const [isCopied, setIsCopied] = useState(_isCopied);
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({
    id: goal.id || "",
    disabled: onHome || isLibrary, // Disable sortable functionality when on home page or in library
  });

  useEffect(() => {
    if (cardRef.current && !height) {
      setHeight(cardRef.current.getBoundingClientRect().height);
    }
  }, [height]);

  useEffect(() => {
    setIsCopied(_isCopied);
  }, [_isCopied]);

  const style = {
    transform: CSS.Translate.toString(transform),
    transition,
    ...(isDragging && !isOverlay && height ? { height: `${height}px` } : {}),
  };

  // Function to handle ref assignments
  const setRefs = (node: HTMLDivElement | null) => {
    // For the sortable functionality
    setNodeRef(node);
    // For measuring height
    cardRef.current = node;
  };

  const onCopyGoal = () => {
    const { id, createdAt, updatedAt, ...goalToCopy } = goal;

    handleCopyGoal({
      ...goalToCopy,
      originalGoal: {
        goalId: goal.id,
        // @ts-expect-error - userId exists but not in base type
        userId: goal.userId,
      },
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    } as Omit<Goal, "id">);
  };

  // Handle card click for navigation
  const handleCardClick = (e: React.MouseEvent) => {
    // Skip navigation if the click originated from elements that should block navigation
    const clickWasOnInteractive = e.isPropagationStopped();

    // Only navigate if this is a direct click on the card (not on interactive elements)
    if (!disableClickThrough && !isLibrary && !clickWasOnInteractive && goal.id) {
      navigate({ to: "/dashboard/goal/$id", params: { id: goal.id } });
    }
  };

  // This ensures that all click events from dropdown and emoji picker are fully stopped
  const preventNavigation = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault(); // Also prevent default to be extra safe
  };

  const cardContent = (
    <Card className="w-full rounded-lg shadow-sm select-none transition-all duration-200 hover:shadow-m bg-white dark:bg-gray-900">
      <CardContent className="p-4">
        <div className="space-y-4">
          {/* Header */}
          <div className="flex justify-between">
            <div className="flex items-start gap-2">
              {(onHome || isLibrary) && goal.symbol && (
                <div className="flex items-center justify-center rounded-full min-w-8 min-h-8 text-2xl">
                  {goal.symbol && goal.symbol.length > 0 ? goal.symbol : DEFAULT_SYMBOL}
                </div>
              )}

              {!onHome && !isLibrary && (
                <div className="flex items-center justify-center rounded-full min-w-8 min-h-8 text-2xl focus-visible:ring-0">
                  {goal.symbol && goal.symbol.length > 0 ? goal.symbol : DEFAULT_SYMBOL}
                </div>
              )}

              <div className="flex-1 space-y-1.5">
                <CardTitle className="font-medium text-lg tracking-tight">{goal.title}</CardTitle>
                {goal.description && (
                  <CardDescription className="mt-1 font-normal leading-relaxed opacity-80">
                    {goal.description}
                  </CardDescription>
                )}

                <div className="flex flex-wrap items-center gap-2 text-sm">
                  {/* Target date pill */}
                  {goal.targetDate && (
                    <span className="flex items-center gap-1.5 bg-primary/5 px-2.5 py-1 rounded-full border border-primary/10">
                      <Calendar className="h-3.5 w-3.5 text-primary/70" />
                      <span className="text-xs font-medium">
                        {format(new Date(goal.targetDate), "MMM d, yyyy")}
                      </span>
                    </span>
                  )}

                  {/* Completion date pill */}
                  {goal.completionDate && (
                    <span className="flex items-center gap-1.5 bg-primary/5 px-2.5 py-1 rounded-full border border-primary/10">
                      <Clock className="h-3.5 w-3.5 text-primary/70" />
                      <span className="text-xs font-medium">
                        {format(new Date(goal.completionDate), "MMM d, yyyy")}
                      </span>
                    </span>
                  )}
                </div>
              </div>
            </div>

            {!onHome && !isLibrary && (
              <div
                data-no-navigate="true"
                className="ml-2"
                onClick={preventNavigation}
                onMouseDown={preventNavigation}
              >
                <ResourceDropdown slug={"goals"} resource={goal} />
              </div>
            )}
          </div>
        </div>
      </CardContent>

      {isLibrary && (
        <CardFooter className="flex items-center gap-2 px-4 py-2.5 border-t border-muted/15">
          <FloatingTooltip
            placement="top"
            disabledTooltip={!isCopied}
            trigger={
              <Button
                data-umami-event="goal-copy-goal"
                variant="outline"
                onClick={(e) => {
                  e.stopPropagation();
                  onCopyGoal();
                }}
                disabled={isCopied}
                className={cn(
                  "flex items-center gap-1.5 rounded-md text-xs font-medium py-1.5 px-3",
                  "bg-primary/5 border-primary/20 hover:bg-primary/10",
                  isCopied && "pointer-events-none opacity-70",
                  "transition-all",
                )}
              >
                {isCopied ? (
                  t("goal_card.added")
                ) : (
                  <>
                    <Plus className="h-3.5 w-3.5" />
                    <span>{t("goal_card.add_goal")}</span>
                  </>
                )}
              </Button>
            }
          >
            <p className="text-sm">{t("goal_card.goal_added_tooltip")}</p>
          </FloatingTooltip>
        </CardFooter>
      )}
    </Card>
  );

  // Wrap with div that handles click instead of Link
  const renderContent = () => {
    if (disableClickThrough || isLibrary || onHome) {
      return cardContent;
    }

    return (
      <div
        onClick={handleCardClick}
        className="cursor-pointer block transition-opacity hover:opacity-90"
      >
        {cardContent}
      </div>
    );
  };

  if (onHome || isLibrary) {
    return (
      <div className="col-span-12 @[768px]:col-span-6 @[1024px]:col-span-4 outline-none">
        {renderContent()}
      </div>
    );
  }

  return (
    <div
      ref={setRefs}
      {...attributes}
      {...listeners}
      style={style}
      className={cn(
        "col-span-12 @[768px]:col-span-6 @[1024px]:col-span-4 outline-none",
        isDragging && "touch-none", // Only apply touch-none when dragging
        isDragging && !isOverlay && "border-2 border-primary/20 bg-primary/5 rounded-lg",
        isOverlay &&
          "bg-primary/10 rounded-lg border-2 border-primary/20 shadow-lg z-250 opacity-75",
      )}
    >
      {(!isDragging || isOverlay) && (
        <ResourceContextMenu
          slug={SCHEMA_NAMES.goals}
          resource={goal}
          enabled={true}
          triggerClassName="w-full"
          trigger={renderContent()}
        />
      )}
    </div>
  );
}
